import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { buildRouteTaskAtom } from "@/store/buildRouteTask";

/**
 * Global top-of-page banner displayed while a build-route Celery task is running.
 * Mirrors the same visual style as `OptimizationProgressBanner`.
 * Rendered null when no task is in progress.
 */
export function BuildRouteProgressBanner() {
  const task = useAtomValue(buildRouteTaskAtom);
  const { t } = useTranslation();

  if (!task) return null;

  const pct = task.progressPct ?? 0;

  return (
    // BUG-420: the running tone follows the tenant's `brand-primary` token (set
    // at runtime by BrandProvider) rather than a hardcoded blue — on a
    // white-label dealer domain a fixed blue reads as someone else's product.
    <div
      data-testid="build-route-progress-banner"
      className="flex items-center gap-3 border-b border-brand-primary/20 bg-brand-primary/5 px-6 py-2 text-sm text-brand-primary"
    >
      <svg
        className="h-4 w-4 shrink-0 animate-spin text-brand-primary"
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
      <span className="font-medium">
        {t("buildRoute.estimatedTime", {
          defaultValue: "Building route... usually takes 1–2 minutes",
        })}
      </span>
      <div className="flex-1">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-brand-primary/20">
          <div
            data-testid="build-route-progress-bar"
            className="h-full rounded-full bg-brand-primary transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      {pct > 0 && <span className="shrink-0 tabular-nums">{pct}%</span>}
    </div>
  );
}
